import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@wds/box';
import { Badge } from '@wds/badge';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';
import { IconLock, IconHeartHand, IconClock } from '@wds/icons';
import { getCookie } from 'sma.common/utils';
import docusignConnect from 'sma.common/docusign-connect';
import { UpgradeModal, DocuSignFinishSetupModal } from './DocuSignModals';
import DocuSignBadge from './DocuSignBadge';
import docusignLogo from './docusign-logo.svg';

const FEATURES = [
    {
        Icon: IconLock,
        label: () => gettext('Secure signatures'),
        description: () => gettext('Protect signed documents'),
    },
    {
        Icon: IconHeartHand,
        label: () => gettext('Simple signing'),
        description: () => gettext('Easier for people to sign'),
    },
    {
        Icon: IconClock,
        label: () => gettext('Track progress'),
        description: () => gettext('See signature status in real time'),
    },
];



const CheckCircleFilled = ({ style }) => (
    <svg width='18' height='18' viewBox='0 0 18 18' aria-hidden='true' style={style}>
        <circle cx='9' cy='9' r='9' fill='#1D9A5C' />
        <path d='M5 9.3 7.6 12 13 6.5' fill='none' stroke='#fff' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

// ---------------------------------------------------------------------------
// Shared modal shell
// ---------------------------------------------------------------------------

const ModalOverlay = ({ onClose, children }) => (
    <div
        style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000,
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
        <div style={{
            background: '#fff', borderRadius: 8,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            width: '100%', maxWidth: 520, margin: 16, position: 'relative',
        }}>
            {children}
        </div>
    </div>
);

const ModalHeader = ({ title, onClose }) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px 16px', borderBottom: '1px solid #e4e8eb',
    }}>
        <Typography variant='body' weight='bold' style={{ fontSize: 18, color: '#1b2a32', margin: 0 }}>
            {title}
        </Typography>
        <button
            type='button'
            onClick={onClose}
            aria-label={gettext('Close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#637381', lineHeight: 1, padding: 4 }}
        >
            &times;
        </button>
    </div>
);

// ---------------------------------------------------------------------------
// Consent / authorization modal (shown when no site connections exist)
// ---------------------------------------------------------------------------

const ConsentModal = ({ onProceed, onClose }) => {
    const [authorized, setAuthorized] = useState(false);

    return (
        <ModalOverlay onClose={onClose}>
            <ModalHeader title={gettext('Connect Docusign')} onClose={onClose} />
            <div style={{ padding: '20px 24px' }}>
                <Typography variant='body' style={{ fontSize: 14, color: '#1b2a32', lineHeight: '1.6', marginBottom: 16 }}>
                    {gettext(
                        'By connecting Docusign, you authorize SurveyMonkey Apply to send form data, '
                        + 'generated documents, signer information, and signature status information to '
                        + 'Docusign to process signature requests. Docusign may process this information '
                        + 'in regions outside your location.',
                    )}
                    <br /><br />
                    {gettext(
                        'Your organization is responsible for its Docusign account, including any terms, '
                        + 'subscriptions, permissions, envelope usage, fees, and applicant notices or consents.',
                    )}
                </Typography>

                <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    cursor: 'pointer', marginBottom: 24,
                }}>
                    <input
                        type='checkbox'
                        checked={authorized}
                        onChange={(e) => setAuthorized(e.target.checked)}
                        style={{ marginTop: 2, flexShrink: 0, width: 18, height: 18, accentColor: '#1D9A5C', cursor: 'pointer' }}
                    />
                    <Typography variant='body' style={{ fontSize: 14, color: '#1b2a32' }}>
                        {gettext("I am authorized to connect my organization's Docusign account.")}
                    </Typography>
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        color='success'
                        disabled={!authorized}
                        onClick={onProceed}
                        style={{ minWidth: 120 }}
                    >
                        {gettext('Proceed')}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Account picker modal (shown when site already has connections)
// ---------------------------------------------------------------------------

const RadioCircle = ({ selected }) => (
    <svg width='18' height='18' viewBox='0 0 18 18' aria-hidden='true' style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx='9' cy='9' r='8' fill='none' stroke={selected ? '#1D9A5C' : '#C4CDD5'} strokeWidth='1.5' />
        {selected && <circle cx='9' cy='9' r='4.5' fill='#1D9A5C' />}
    </svg>
);

const AccountPickerModal = ({
    connections, onContinueWithExisting, onSignWithNew, onClose, assigning,
}) => {
    const [useNew, setUseNew] = useState(false);
    const [selectedId, setSelectedId] = useState(connections[0]?.id ?? null);

    const handleContinue = () => {
        if (useNew) {
            onSignWithNew();
        } else {
            onContinueWithExisting(selectedId);
        }
    };

    return (
        <ModalOverlay onClose={onClose}>
            <ModalHeader title={gettext('Select Account')} onClose={onClose} />
            <div style={{ padding: '20px 24px' }}>
                <Typography variant='body' style={{ fontSize: 14, color: '#637381', marginBottom: 20 }}>
                    {gettext("Choose how you'd like to connect your Docusign account to get started.")}
                </Typography>

                {/* Option A: continue with an existing site connection (no re-auth) */}
                <label
                    style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '14px 16px', cursor: 'pointer',
                        border: '1px solid', borderRadius: 6, marginBottom: 10,
                        borderColor: !useNew ? '#1D9A5C' : '#e4e8eb',
                        background: !useNew ? '#f0faf5' : '#fff',
                    }}
                >
                    <input type='radio' name='ds-picker' checked={!useNew} onChange={() => setUseNew(false)} style={{ display: 'none' }} />
                    <RadioCircle selected={!useNew} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='body' weight='bold' style={{ fontSize: 14, color: '#1b2a32' }}>
                            {gettext('Continue with your existing account(s)')}
                        </Typography>
                        <Typography variant='body' style={{ fontSize: 13, color: '#637381', marginBottom: 8 }}>
                            {gettext('Authorize the integration using your current Docusign session.')}
                        </Typography>
                        {!useNew && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Typography variant='body' style={{ fontSize: 13, color: '#637381', flexShrink: 0 }}>
                                    {gettext('Signed in with')}
                                </Typography>
                                <select
                                    value={selectedId ?? ''}
                                    onChange={(e) => setSelectedId(Number(e.target.value))}
                                    style={{
                                        flex: 1, padding: '6px 10px', border: '1px solid #C4CDD5',
                                        borderRadius: 4, fontSize: 13, color: '#1b2a32', background: '#fff',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {connections.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.display_name || c.account_email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </label>

                {/* Option B: connect a brand-new account via OAuth */}
                <label
                    style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '14px 16px', cursor: 'pointer',
                        border: '1px solid', borderRadius: 6, marginBottom: 24,
                        borderColor: useNew ? '#1D9A5C' : '#e4e8eb',
                        background: useNew ? '#f0faf5' : '#fff',
                    }}
                >
                    <input type='radio' name='ds-picker' checked={useNew} onChange={() => setUseNew(true)} style={{ display: 'none' }} />
                    <RadioCircle selected={useNew} />
                    <div>
                        <Typography variant='body' weight='bold' style={{ fontSize: 14, color: '#1b2a32' }}>
                            {gettext('Sign in with a new account')}
                        </Typography>
                        <Typography variant='body' style={{ fontSize: 13, color: '#637381' }}>
                            {gettext('Use another Docusign account to authorize the integration.')}
                        </Typography>
                    </div>
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        color='success'
                        onClick={handleContinue}
                        disabled={assigning}
                        style={{ minWidth: 120 }}
                    >
                        {assigning
                            ? <><ProgressCircle continuous size='sm' color='light' />&nbsp;{gettext('Saving...')}</>
                            : gettext('Continue')}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

const DocuSignTaskCard = ({
    connectUrl,
    connectionsUrl,
    assignUrl,
    isPlanBelowLimit,
    upgradeUrl,
}) => {
    // Program-specific connection status — fetched on mount from connectionsUrl
    const [loadingStatus, setLoadingStatus] = useState(!!connectionsUrl);
    const [isConnected, setIsConnected] = useState(false);
    const [accountName, setAccountName] = useState('');
    // All site-level connections (populated by the mount fetch)
    const [siteConnections, setSiteConnections] = useState([]);

    const [modal, setModal] = useState(null); // null | 'consent' | 'picker'
    const [assigning, setAssigning] = useState(false);
    const [assignError, setAssignError] = useState('');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showFallbackInfo, setShowFallbackInfo] = useState(false);

    // On mount: fetch the connections list and determine whether THIS program
    // already has a connection assigned (current_connection_id).
    useEffect(() => {
        if (!connectionsUrl) {
            setLoadingStatus(false);
            return;
        }
        let cancelled = false;

        fetch(connectionsUrl, { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then(async (data) => {
                if (cancelled) return;
                const list = data.connections || [];
                setSiteConnections(list);

                const currentId = data.current_connection_id;
                const current = currentId != null ? list.find((c) => c.id === currentId) : null;
                if (current) {
                    // The assigned connection is active — show connected state.
                    setIsConnected(true);
                    setAccountName(current.display_name || current.account_email || '');
                } else {
                    setIsConnected(false);
                    setAccountName('');
                }

                // After a new OAuth connection completes, the backend redirects back
                // with ?ds_just_connected=1.  Show the manual-fallback info note inline.
                const params = new URLSearchParams(window.location.search);
                if (params.get('ds_just_connected')) {
                    setShowFallbackInfo(true);
                    const cleanSearch = window.location.search
                        .replace(/[?&]ds_just_connected=[^&]*/g, '')
                        .replace(/^&/, '?');
                    // Preserve the hash — this page's task/form view is a hash-based
                    // route (e.g. #round/9451/task/1/form/2); dropping it here would
                    // bounce the admin back to the round overview.
                    window.history.replaceState(null, '', window.location.pathname + cleanSearch + window.location.hash);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsConnected(false);
                    setAccountName('');
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingStatus(false);
            });

        return () => { cancelled = true; };
    }, [connectionsUrl, assignUrl]);

    // Mirror site-level connection status changes that happen within the same page
    // session (e.g. an admin disconnects from a different component on this page).
    // Cross-tab disconnects are NOT reflected here — with the 30s poll removed,
    // fetchStatus() only runs on page load, so onStatusChange won't fire after that.
    useEffect(() => {
        return docusignConnect.onStatusChange((state) => {
            if (!state.connected) {
                setIsConnected(false);
                setAccountName('');
                setSiteConnections([]);
            }
        });
    }, []);

    // "Connect to Docusign" button clicked — program has no connection yet
    const handleConnectClick = useCallback(() => {
        // siteConnections already loaded from mount fetch
        if (siteConnections.length > 0) {
            setModal('picker');
        } else {
            setModal('consent');
        }
    }, [siteConnections]);

    // OAuth path (consent modal → proceed, or picker → "Sign with new account")
    const handleProceedOAuth = () => {
        window.location.href = connectUrl;
    };

    // Assign an existing site connection to this program (no re-auth)
    const handleContinueWithExisting = async (connectionId) => {
        if (!assignUrl || !connectionId) return;
        setAssigning(true);
        setAssignError('');
        try {
            const resp = await fetch(assignUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({ connection_id: connectionId }),
            });
            if (resp.ok) {
                const assigned = siteConnections.find((c) => c.id === connectionId);
                setIsConnected(true);
                setAccountName(assigned ? (assigned.display_name || assigned.account_email || '') : '');
                setShowFallbackInfo(true);
                setModal(null);
            } else {
                const data = await resp.json().catch(() => ({}));
                setAssignError(data.message || gettext('Failed to assign account. Please try again.'));
            }
        } catch (_e) {
            setAssignError(gettext('Failed to assign account. Please try again.'));
        } finally {
            setAssigning(false);
        }
    };

    const showUpgrade = !isConnected && isPlanBelowLimit;

    return (
        <>
            {showUpgradeModal && (
                <UpgradeModal upgradeUrl={upgradeUrl} onClose={() => setShowUpgradeModal(false)} />
            )}
            {modal === 'consent' && (
                <ConsentModal
                    onProceed={handleProceedOAuth}
                    onClose={() => setModal(null)}
                />
            )}
            {modal === 'picker' && (
                <AccountPickerModal
                    connections={siteConnections}
                    assigning={assigning}
                    onContinueWithExisting={handleContinueWithExisting}
                    onSignWithNew={() => setModal('consent')}
                    onClose={() => setModal(null)}
                />
            )}
            {showFallbackInfo && (
                <DocuSignFinishSetupModal accountName={accountName} onClose={() => setShowFallbackInfo(false)} />
            )}
            {assignError && (
                <div style={{
                    background: '#FFF8F7', border: '1px solid #FCECEA',
                    borderRadius: 4, padding: '8px 12px', marginBottom: 8,
                    fontSize: 13, color: '#D82C0D',
                }}>
                    {assignError}
                </div>
            )}

            <Box
                className='docusign-task-card'
                style={{
                    backgroundColor: showUpgrade ? '#FEF2CC' : '#E6F9FA',
                    border: showUpgrade ? '1px solid #FEF2CC' : '1px solid #E6F9FA',
                    borderRadius: '4px',
                }}
                p={5}
            >
                {isConnected ? (
                    /* ── Connected state: compact layout matching Figma ── */
                    <>
                        {/* Header: small inline logo + title + info tooltip */}
                        <Box display='flex' alignItems='center' gap={2} mb={2}>
                            <img src={docusignLogo} alt='' height={20} style={{ flexShrink: 0 }} />
                            <strong style={{ fontSize: '16px', fontWeight: 700, color: '#1B2A32' }}>
                                {gettext('Docusign')}
                            </strong>
                            <i
                                className='nc-icon-mini ui-e_round-e-info ml-1'
                                data-toggle='tooltip'
                                data-placement='top'
                                title={`${gettext('Protect signed documents')} · ${gettext('Easier for people to sign')} · ${gettext('See signature status in real time')}`}
                            />
                        </Box>
                        {/* Description */}
                        <Typography variant='body' style={{ lineHeight: '1.6', marginBottom: 12 }}>
                            {gettext('Add docusign to your form so people can sign as part of their response.')}
                        </Typography>
                        {/* Connected badge + green dot + email — all on one row */}
                        <Box display='flex' alignItems='center' gap={2}>
                            <DocuSignBadge variant='connected' />
                            <Box display='flex' alignItems='center' gap={1} style={{ minWidth: 0 }}>
                                <CheckCircleFilled style={{ flexShrink: 0 }} />
                                <Typography
                                    variant='body'
                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '14px' }}
                                    title={interpolate(gettext('Account: %(name)s'), { name: accountName }, true)}
                                >
                                    {interpolate(gettext('Account: %(name)s'), { name: accountName }, true)}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                ) : (
                    /* ── Disconnected state: promo layout ── */
                    <>
                        <Box display='flex' alignItems='flex-start' justifyContent='space-between' mb={3}>
                            <Box display='flex' flexDirection='column' alignItems='flex-start' gap={2}>
                                <img src={docusignLogo} alt='' height={32} />
                                <Box display='flex' alignItems='center' gap={2}>
                                    <strong style={{ fontSize: '18px', fontWeight: 700, color: '#1B2A32' }}>
                                        {gettext('Introducing Docusign')}
                                    </strong>
                                    <Badge color='success' size='sm'>{gettext('New')}</Badge>
                                </Box>
                            </Box>

                            {/* Only show a button/spinner if not connected.
                                Existing connections keep working regardless of plan — only
                                starting a NEW connection is plan-gated. */}
                            {!loadingStatus && (
                                showUpgrade ? (
                                    <Button color='upgrade' onClick={() => setShowUpgradeModal(true)}>
                                        <IconLock size='sm' />
                                        <span style={{ display: 'inline-block', width: 6 }} />
                                        {gettext('Upgrade Plan')}
                                    </Button>
                                ) : (
                                    <Button color='success' onClick={handleConnectClick}>
                                        {siteConnections.length > 0 ? gettext('Select Account') : gettext('Connect to Docusign')}
                                    </Button>
                                )
                            )}
                            {loadingStatus && <ProgressCircle continuous size='sm' />}
                        </Box>

                        <Box mb={4}>
                            <Typography variant='body' style={{ lineHeight: '1.6', fontSize: '15px' }}>
                                {showUpgrade ? gettext('Collect signatures directly from your forms.') : gettext(
                                    "You'll need a docusign account to collect signatures. "
                                    + 'Connect one or create a new account to get started. '
                                    + 'Each document sent for signature uses one envelope from your Docusign plan.',
                                )}
                            </Typography>
                        </Box>
                        <Box display='flex' flexDirection='row' gap={6}>
                            {FEATURES.map(({ Icon, label, description }) => (
                                <Box key={label()} display='flex' flexDirection='row' alignItems='flex-start' gap={2} style={{ flex: 1 }}>
                                    <Icon size='md' color='dark' style={{ flexShrink: 0, marginTop: 2 }} />
                                    <Box display='flex' flexDirection='column' gap={1}>
                                        <Typography variant='body' style={{ fontWeight: 600, fontSize: '14px', color: '#1B2A32', lineHeight: '1.3' }}>
                                            {label()}
                                        </Typography>
                                        <Typography variant='body' size='sm' color='darkMuted' style={{ lineHeight: '1.4', fontSize: '14px' }}>
                                            {description()}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        </>
    );
};

DocuSignTaskCard.propTypes = {
    connectUrl: PropTypes.string,
    connectionsUrl: PropTypes.string,
    assignUrl: PropTypes.string,
    isPlanBelowLimit: PropTypes.bool,
    upgradeUrl: PropTypes.string,
};

DocuSignTaskCard.defaultProps = {
    connectUrl: '',
    connectionsUrl: '',
    assignUrl: '',
    isPlanBelowLimit: false,
    upgradeUrl: '',
};

export default DocuSignTaskCard;
